import { DotsVertical, Target01, X } from '@untitledui/icons';
import { useState } from 'react';
import { IconButton } from '../../../components/buttons/IconButton';
import DropDownMenu from '../../../components/general/DropDownMenu';
import MenuItemTile, { type MenuItemTileProps } from '../../../components/general/MenuItemTile';
import { Text } from '../../../components/general/Text';
import { VehicleConnectionIndicator } from '../../../components/general/VehicleConnectionIndicator';
import { getServerNameCode } from '../../../config/gcs/gcsConfigs';
import { en } from '../../../locales/en';
import { useGcsStore } from '../../../store/useGcsStore';
import type { VehicleId } from '../../../types/gcs/vehicle';
import { getShortMissionId, getShortVehicleId } from '../../../utils/formatters';
import VehicleStatusTile from '../side-panel/VehicleStatusTile';
import VehicleStatusCategoryIcon from './VehicleStatusCategoryIcon';
import { selectVehicleStatusCategory } from '../../../store/selectors/selectVehicleStatusCategory';
interface VehiclePanelHeaderProps {
  gcsId: string;
  vehicleId: VehicleId;
  onFocusVehicle: () => void;
  onClose: () => void;
  menuItems?: MenuItemTileProps[];
}

function VehiclePanelHeader({ gcsId, vehicleId, onFocusVehicle, onClose, menuItems = [] }: VehiclePanelHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const vehicleLabel = getShortVehicleId(vehicleId);
  const centerNameCode = getServerNameCode(gcsId);

  const launchpad = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    const launchpadId = gcsData?.vehicleLaunchpadMap.get(vehicleId);
    if (!launchpadId) return undefined;
    return gcsData?.launchpads.data?.find((l) => l.id === launchpadId);
  });

  const vehicleStatusCategory = useGcsStore((state) => selectVehicleStatusCategory(state.data, gcsId, vehicleId));

  const missionId = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    const mission = gcsData?.liveMissionsMap.get(vehicleId);
    return mission?.missionId;
  });

  const shortMissionId = missionId ? getShortMissionId(missionId) : null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}
    >
      {/* Leading icon */}
      <div style={{ flexShrink: 0 }}>
        {vehicleStatusCategory && <VehicleStatusCategoryIcon vehicleStatusCategory={vehicleStatusCategory} size={48} />}
      </div>

      {/* Vehicle and center name */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minWidth: 0,
          gap: '8px',
          alignItems: 'flex-start'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            minWidth: 0
          }}
        >
          <Text
            variant='h2'
            style={{
              whiteSpace: 'nowrap',
              flex: '0 0 auto'
            }}
          >
            {vehicleLabel}
          </Text>
          <div
            style={{
              flex: '0 1 auto',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'flex-start'
            }}
          >
            <VehicleStatusTile gcsId={gcsId} vehicleId={vehicleId} />
          </div>
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
            <VehicleConnectionIndicator gcsId={gcsId} vehicleId={vehicleId} />
          </div>
        </div>
        <Text
          variant='body2'
          style={{
            color: 'var(--text-secondary)'
          }}
        >
          {centerNameCode}
          {launchpad?.letter ? ` | ${en.vehiclePanelHeader.pad} ${launchpad.letter}` : ''}
          {shortMissionId ? ` | ${shortMissionId}` : ''}
        </Text>
      </div>

      {/* Action buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flex: '0 0 auto',
          flexShrink: 0
        }}
      >
        {/* Focus on vehicle button */}
        <IconButton icon={Target01} onClick={onFocusVehicle} title={en.vehiclePanelHeader.focusOnVehicle} />

        {/* Overflow menu button */}
        <div style={{ position: 'relative' }}>
          <IconButton
            icon={DotsVertical}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            title={en.vehiclePanelHeader.moreOptions}
          />

          <DropDownMenu
            isOpen={isMenuOpen && menuItems.length > 0}
            onClose={() => setIsMenuOpen(false)}
            top='100%'
            right='0'
            position='right'
          >
            {menuItems.map((item, index) => (
              <MenuItemTile
                key={index}
                {...item}
                onClick={() => {
                  item.onClick?.();
                  setIsMenuOpen(false);
                }}
              />
            ))}
          </DropDownMenu>
        </div>

        {/* Close button */}
        <IconButton icon={X} onClick={onClose} title={en.vehiclePanelHeader.close} />
      </div>
    </div>
  );
}

export default VehiclePanelHeader;
